import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Forms/Inputs/Input'
import Select from 'common/components/Forms/Inputs/Select'
import { UploadPhoto } from 'common/components/Upload/UploadPhoto'
import { useGet, usePost } from 'common/hooks/useApi'
import { IPaginatedResponse } from 'common/types/api.types'
import { handleError } from 'common/utils/validation/errorHandler'
import { errorMessages } from 'common/utils/validation/messages'
import { useFormik } from 'formik'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { toast } from 'react-toastify'
import { ApiPathsWiki } from 'services/community/wiki.api'
import { IBot, ICommunityWiki } from 'services/community/wiki.types'
import { usePartialFilters } from 'services/filters/filters.service'
import styled from 'styled-components'
import { KeyedMutator } from 'swr'
import * as Yup from 'yup'

const wikiTypes = [
  { label: 'News', value: 'news' },
  { label: 'Question', value: 'question' },
]

const initialWikiState = {
  botId: '',
  type: '',
  title: '',
  description: '',
  // disable_comments: false,
  link: '',
  media: '',
  communityTag: '',
  countryId: '',
  // pollAnswers: [],
  // vesselName: '',
  // registeredOwner: '',
  // vesselFlag: '',
}

const WikiSchema = Yup.object().shape({
  botId: Yup.string().required(errorMessages.isRequired),
  type: Yup.string().required(errorMessages.isRequired),
  title: Yup.string().required(errorMessages.isRequired).max(400, 'Title is too long'),
  description: Yup.string().required(errorMessages.isRequired),
  // disable_comments: Yup.boolean(),
  link: Yup.string().optional(),
  media: Yup.mixed()
    .optional()
    .test(
      'fileSize',
      'File too large',
      (value?: File) => !value || (value && value.size <= 1024 * 1024 * 5), // 5MB
    )
    .test(
      'fileFormat',
      'Unsupported Format',
      (value?: File) => !value || (value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type)),
    ),
  communityTag: Yup.string().optional(),
  // countryId: Yup.string().optional(),
  // pollAnswers: Yup.array().of(Yup.string()).optional(),
  // vesselName: Yup.string().optional(),
  // registeredOwner: Yup.string().optional(),
  // vesselFlag: Yup.string().optional(),
})

type CreateWikiForm = {
  botId: string
  type: string
  title: string
  description: string
  link: string
  media: string
  communityTag: string
  countryId: string
}

interface IProps {
  mutate: KeyedMutator<IPaginatedResponse<ICommunityWiki[]>>
}

export const CommunityAdminAddWikiDialog = ({ mutate }: IProps) => {
  const [visible, setVisible] = useState(false)

  const onSubmit = async (data: CreateWikiForm) => {
    const formData = new FormData()
    formData.append('title', data.title)
    formData.append('description', data.description)
    data.botId && formData.append('botId', data.botId)

    data.communityTag && formData.append('communityTag', data.communityTag)

    data.link && formData.append('link', data.link)
    data.media && formData.append('media', data.media)

    await addBotWiki(formData, {
      onError(err) {
        handleError(err)
      },
      async onSuccess() {
        toast.success('Wiki was added')
        setVisible(false)
        mutate()
      },
    })
  }

  const { setFieldValue, values, errors, touched, setFieldTouched, submitForm } = useFormik<CreateWikiForm>({
    initialValues: initialWikiState,
    enableReinitialize: true,
    validationSchema: WikiSchema,
    onSubmit,
  })

  const { trigger: addBotWiki, isMutating: isUpdating } = usePost(
    ApiPathsWiki.addBotWiki(values.type as 'news' | 'question'),
  )

  const { data } = useGet<IPaginatedResponse<IBot[]>>(ApiPathsWiki.bots)
  const botsOptions =
    data?.data.map((bot) => ({
      label: `${bot.candidate.user.firstName} ${bot.candidate.user.lastName}`,
      value: bot.id,
    })) || []
  const {
    data: { countries = [] },
  } = usePartialFilters(['countries'])
  const countryOptions: {
    id: string
    name: string
    phonePrefix: string
    countryCode: string
    componentIcon: JSX.Element
  }[] = countries
    .map((country) => {
      if (country.name !== 'Netherlands Antilles') {
        return {
          componentIcon: <ReactCountryFlag className="w-auto" countryCode={country.countryCode} svg />,
          ...country,
        }
      } else {
        return {
          id: 'delete',
          name: 'delete',
          phonePrefix: 'delete',
          countryCode: 'delete',
          componentIcon: <></>,
        }
      }
    })
    .filter((country) => country.name !== 'delete')

  const handleClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setVisible(true)
  }

  const handleHide = (e?: React.SyntheticEvent) => {
    e && e.preventDefault()
    setVisible(false)
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldTouched(e.target.name, false)
    setFieldValue(e.target.name, e.target.value)
  }

  const onDropdownChange = (e: any) => {
    setFieldTouched(e.target.name, false)
    setFieldValue(e.target.name, e.value)
  }
  const selectedCommunityTag = countryOptions.find((c) => c.name === values.communityTag)?.name
  return (
    <>
      <Button size="large" className="w-10rem" onClick={handleClick}>
        Add wiki
      </Button>
      <StyledDialog
        header={<p className="font-semibold text-2xl ">Add Wiki</p>}
        visible={visible}
        onHide={handleHide}
        draggable={false}
      >
        <div className="flex justify-content-center">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              submitForm()
            }}
            className="w-full flex justify-content-center"
          >
            <Div>
              <div className="flex flex-column">
                <Dropdown
                  name="botId"
                  value={values.botId}
                  options={botsOptions}
                  onChange={onDropdownChange}
                  placeholder="Select a Bot"
                  className={touched?.botId && errors?.botId ? 'p-invalid' : ''}
                />
                {touched?.botId && errors?.botId && (
                  <small className="p-error">{typeof errors.botId === 'string' ? errors.botId : ''}</small>
                )}
              </div>
              <div className="flex flex-column">
                <Dropdown
                  name="type"
                  value={values.type}
                  options={wikiTypes}
                  onChange={onDropdownChange}
                  placeholder="Select Wiki Type"
                  className={touched?.type && errors?.type ? 'p-invalid' : ''}
                />
                {touched?.type && errors?.type && typeof errors.type === 'string' && (
                  <small className="p-error">{errors.type}</small>
                )}
              </div>

              <Select
                label="Community tag"
                value={selectedCommunityTag}
                options={countryOptions}
                name="communityTag"
                onChange={onDropdownChange}
                placeholder="Select community tag"
                filterBy="label"
                filter
                optionValue="name"
                showFilterClear
              />

              <Input
                name="title"
                label="Title"
                placeholder="Type here"
                onChange={onChange}
                error={touched?.title && typeof errors?.title === 'string' && errors.title}
              />

              <Input
                name="description"
                label="Description"
                placeholder="Type here"
                onChange={onChange}
                error={touched?.description && typeof errors?.description === 'string' && errors.description}
              />
              {values.type === 'news' && (
                <Input
                  name="link"
                  label="Link"
                  placeholder="Type here"
                  onChange={onChange}
                  error={touched?.link && typeof errors?.link === 'string' && errors.link}
                />
              )}

              {values.type === 'question' && (
                <UploadPhoto
                  name="media"
                  file={values.media}
                  handleChange={(e: any) => {
                    setFieldTouched('media', false)
                    setFieldValue('media', e?.files[0] || null)
                  }}
                  error={touched?.media && (errors?.media as string)}
                />
              )}

              {/* <Input
                      name="vesselName"
                      label="Vessel Name"
                      placeholder="Type here"
                      onChange={onChange}
                      error={touched?.vesselName && typeof errors?.vesselName === 'string' && errors.vesselName}
                    />
                    <Input
                      name="registeredOwner"
                      label="Registered Owner"
                      placeholder="Type here"
                      onChange={onChange}
                      error={
                        touched?.registeredOwner &&
                        typeof errors?.registeredOwner === 'string' &&
                        errors.registeredOwner
                      }
                    /> */}
              {/* <Input
                      name="vesselFlag"
                      label="Vessel Flag"
                      placeholder="Type here"
                      onChange={onChange}
                      error={touched?.vesselFlag && typeof errors?.vesselFlag === 'string' && errors.vesselFlag}
                    /> */}
              <div className="mt-5 flex gap-2 justify-content-center">
                <Button
                  className="secondary_100 flex-1"
                  onClick={handleHide}
                  variant="ghost"
                  size="large"
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button className="flex-1" type="submit" size="large" isLoading={isUpdating}>
                  Add Wiki
                </Button>
              </div>
            </Div>
          </form>
        </div>
      </StyledDialog>
    </>
  )
}

const Div = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  padding: 3rem;
`

const StyledDialog = styled(Dialog)`
  width: 720px;
`
